import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Button, Input } from 'reactstrap';
import { useTenantContext } from '../../auth/useTenantContext';
import { useSetColliImballo } from '../../api/pickListLifecycle';

export interface PickListColliImballoBarProps {
  pickListId: number;
  status: string;
  colli: number | null;
  imballo: string | null;
  /** Valorizzato ⇒ lista già inviata all'ERP: i valori sono congelati. */
  exportedAt: string | null;
  /** Capo magazziniere (o platform-admin). Gli altri vedono in sola lettura. */
  canEdit: boolean;
  warehouseIdHint?: number;
}

/**
 * Colli e imballo registrati dal capo magazziniere a fine prelievo.
 *
 * Compare solo su liste evase o confermate, cioè quando il conteggio ha senso.
 *
 * **Perché è in evidenza quando manca:** finché i due valori non ci sono la
 * lista NON viene inviata a Book. Se il campo vuoto passasse inosservato, la
 * lista resterebbe ferma in silenzio e il problema si scoprirebbe solo quando
 * l'amministrazione nota che manca una consegna.
 *
 * Per i COLLI, `null` e `0` sono stati diversi: `null` è "nessuno ha ancora
 * contato", `0` è "contati zero colli" — un dato legittimo che sblocca l'invio.
 *
 * L'IMBALLO è invece una scelta fra due voci. La tendina parte VUOTA: una
 * tipologia preselezionata permetterebbe di salvare senza decidere, e
 * svuoterebbe di senso il blocco all'invio.
 */
export function PickListColliImballoBar({
  pickListId, status, colli, imballo, exportedAt, canEdit, warehouseIdHint,
}: PickListColliImballoBarProps) {
  const { t } = useTranslation('picklist');
  const { token, tenantCode } = useTenantContext();
  const mut = useSetColliImballo(token, tenantCode, warehouseIdHint);

  const [editing, setEditing] = useState(false);
  const [colliVal, setColliVal] = useState<string>(colli !== null ? String(colli) : '');
  const [imballoVal, setImballoVal] = useState<string>(imballo ?? '');
  const [error, setError] = useState<string | null>(null);

  // Anche `archived`: una lista partita SENZA i due dati deve dirlo, altrimenti
  // non resta traccia visibile del fatto che a Book sono arrivati vuoti.
  if (status !== 'fulfilled' && status !== 'confirmed' && status !== 'archived') return null;

  const frozen = exportedAt !== null;
  const missing = colli == null || imballo == null;
  // Su una lista gia' partita "mancante" non e' un'azione da fare ma un fatto
  // avvenuto: mostrarla come "Da inserire" inviterebbe a un'operazione che il
  // backend rifiuta.
  const missingAndGone = missing && frozen;

  const save = async () => {
    setError(null);
    const c = Number(colliVal);
    // La scelta vuota cade qui insieme ai colli non validi: esiste nella tendina
    // come punto di partenza, ma non deve mai diventare un dato salvato.
    if (colliVal.trim() === '' || imballoVal === ''
        || !Number.isInteger(c) || c < 0) {
      setError(t('colliImballo.invalid'));
      return;
    }
    try {
      await mut.mutateAsync({ pickListId, colli: c, imballo: imballoVal });
      setEditing(false);
    } catch (err: unknown) {
      // Le mutation di pick-list lanciano LifecycleApiError, che espone `code`
      // e `detail` DIRETTAMENTE (non annidati sotto `body`): leggerli dal posto
      // sbagliato farebbe cadere il messaggio sul generico "Errore".
      const e = err as { code?: string; detail?: string; message?: string };
      setError(e?.code === 'colli_imballo_frozen'
        ? t('colliImballo.frozen')
        : e?.detail ?? t('colliImballo.error'));
    }
  };

  return (
    <div className={'d-flex flex-column gap-2 mb-3 p-2 rounded border bg-body-tertiary'
                    + (missing && !frozen ? ' vp-colli-missing' : '')}>
      <div className="d-flex align-items-center gap-3 flex-wrap">
        <span className="fw-semibold small">{t('colliImballo.label')}</span>

        {editing ? (
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Input type="number" min={0} step={1} style={{ width: 110 }}
                   aria-label={t('colliImballo.colli')}
                   placeholder={t('colliImballo.colli')}
                   value={colliVal} onChange={(e) => setColliVal(e.target.value)} />
            <Input type="select" style={{ width: 150 }}
                   aria-label={t('colliImballo.imballo')}
                   value={imballoVal} onChange={(e) => setImballoVal(e.target.value)}>
              <option value="">{t('colliImballo.choose')}</option>
              {/* I valori sono quelli che legge Book, non etichette tradotte. */}
              <option value="Scatole">{t('colliImballo.scatole')}</option>
              <option value="Bancali">{t('colliImballo.bancali')}</option>
            </Input>
            <Button color="primary" size="sm" disabled={mut.isPending} onClick={() => void save()}>
              {t('colliImballo.save')}
            </Button>
            <Button color="light" size="sm" onClick={() => { setEditing(false); setError(null); }}>
              {t('colliImballo.cancel')}
            </Button>
          </div>
        ) : (
          <>
            {missingAndGone ? (
              <span className="small text-muted">{t('colliImballo.notRecorded')}</span>
            ) : missing ? (
              // In evidenza: finché manca, la lista non parte.
              <Badge color="warning" className="bg-warning-subtle text-warning">
                {t('colliImballo.missing')}
              </Badge>
            ) : (
              <span className="small">
                <strong>{colli}</strong> {t('colliImballo.colli')} · <strong>{imballo}</strong>
              </span>
            )}
            {canEdit && !frozen && (
              // Pulsante pieno quando il dato manca: un link testuale accanto a
              // un badge non si distingue dalle altre barre informative, ed e'
              // esattamente cosi' che e' passato inosservato.
              <Button color={missing ? 'warning' : 'link'} size="sm"
                      className={missing ? undefined : 'p-0'}
                      onClick={() => { setEditing(true); setError(null); }}>
                <i className="bi bi-pencil me-1" />
                {missing ? t('colliImballo.set') : t('colliImballo.edit')}
              </Button>
            )}
          </>
        )}
      </div>

      {missing && !frozen && !editing && (
        <span className="text-muted small">
          <i className="bi bi-exclamation-triangle me-1" />
          {t('colliImballo.blocksExport')}
        </span>
      )}
      {frozen && (
        <span className="text-muted small">
          <i className="bi bi-lock me-1" />
          {t('colliImballo.frozenHint')}
        </span>
      )}
      {error && <Alert color="danger" className="mb-0 py-1 px-2 small" role="alert">{error}</Alert>}
    </div>
  );
}
